import React, { useState } from 'react'
import { Box, Button, InputLabel, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material'
import { type SlotEmailSettings } from '../../../../models/SlotEmailSettings'
import { Controller, useWatch, type Control } from 'react-hook-form'
import { grey } from '../../../../colors'
import { NextEmailSendRow } from '../NextEmailSendRow'
import api from '../../../../utils/api'
import { notification } from '../../../../components/Notifications'
import { EMAIL_TYPE_TO_TASK } from '../../index'
import { getTaskTriggerToastMessage } from '../../../../utils/taskTriggerToast'

interface MergedField {
  field: SlotEmailSettings & { id: string }
  index: number
}

interface MergedSurveyNotificationsCardProps {
  fields: MergedField[]
  control: Control<any>
  isReadOnly?: boolean
  isSlotPublishedOrLater?: boolean
  showDevTools?: boolean
  slotId?: string
}

const daysBeforeOptions = [
  { value: 1, label: '1 Day Before' },
  { value: 2, label: '2 Days Before' },
  { value: 3, label: '3 Days Before' },
  { value: 4, label: '4 Days Before' },
  { value: 5, label: '5 Days Before' },
  { value: 6, label: '6 Days Before' },
  { value: 7, label: '1 Week Before' },
  { value: 14, label: '2 Weeks Before' },
  { value: 21, label: '3 Weeks Before' },
  { value: 30, label: '1 Month Before' }
]

const reminderIntervalOptions = [
  { value: 1, label: 'Daily' },
  { value: 2, label: 'Every other day' },
  { value: 3, label: 'Every 3 days' },
  { value: 4, label: 'Every 4 days' },
  { value: 5, label: 'Every 5 days' },
  { value: 6, label: 'Every 6 days' },
  { value: 7, label: 'Weekly' },
  { value: 14, label: 'Every 2 weeks' },
  { value: 21, label: 'Every 3 weeks' },
  { value: 30, label: 'Monthly' }
]

interface SettingRowProps {
  field: SlotEmailSettings & { id: string }
  index: number
  control: Control<any>
  isReadOnly: boolean
  isSlotPublishedOrLater: boolean
  showDevTools?: boolean
  devTaskName?: string
  slotId?: string
}

const SettingRow: React.FC<SettingRowProps> = ({ field, index, control, isReadOnly, isSlotPublishedOrLater, showDevTools, devTaskName, slotId }) => {
  const isEnabled = useWatch({ control, name: `settings.${index}.enabled` }) ?? field.enabled
  const [isTriggering, setIsTriggering] = useState(false)

  const handleTrigger = async (): Promise<void> => {
    if (!devTaskName) return
    setIsTriggering(true)
    try {
      const result = await api.post<{ taskId?: string, task_id?: string, message?: string }>(`/api/tasks/trigger/${devTaskName}/`, { slot_id: slotId })
      notification(getTaskTriggerToastMessage(result.data), { variant: 'success' })
    } catch {
      notification(`Failed to trigger task: ${devTaskName}`, { variant: 'error' })
    } finally {
      setIsTriggering(false)
    }
  }

  return (
    <Stack key={field.id} spacing={2}>
      <Box display="flex" alignItems="center" gap={2}>
        <Controller
          name={`settings.${index}.enabled`}
          control={control}
          render={({ field: { onChange, value } }) => (
            <Switch checked={value} onChange={onChange} disabled={isReadOnly} />
          )}
        />
        <Typography variant="p5" sx={{ fontWeight: 'bold' }}>{field.title}</Typography>
        {showDevTools === true && devTaskName != null && (
          <Button variant="outlined" size="small" disabled={isTriggering} onClick={() => { void handleTrigger() }} sx={{ ml: 'auto' }}>
            {isTriggering ? 'Sending...' : 'Send email'}
          </Button>
        )}
      </Box>
      <Typography variant="p5">{field.description}</Typography>

      {field.settingType === 'DAYS_BEFORE' && (
        <Box minHeight={72}>
          <InputLabel sx={{ marginBottom: 2, color: grey[950] }}>Schedule</InputLabel>
          <Controller
            name={`settings.${index}.daysBefore`}
            control={control}
            render={({ field: formField }) => (
              <TextField select fullWidth {...formField} value={formField.value ?? ''} disabled={isReadOnly}>
                {daysBeforeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            )}
          />
        </Box>
      )}

      {field.settingType === 'REMINDER_INTERVAL' && (
        <Box minHeight={72}>
          <InputLabel sx={{ marginBottom: 2, color: grey[950] }}>Frequency</InputLabel>
          <Controller
            name={`settings.${index}.reminderInterval`}
            control={control}
            render={({ field: formField }) => (
              <TextField select fullWidth {...formField} value={formField.value ?? ''} disabled={isReadOnly}>
                {reminderIntervalOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            )}
          />
        </Box>
      )}

      <NextEmailSendRow
        isSlotPublishedOrLater={isSlotPublishedOrLater}
        isEnabled={isEnabled}
        nextEmailPushTimestamp={field.nextEmailPushTimestamp}
        lastEmailSentTimestamp={field.lastEmailSentTimestamp}
      />
    </Stack>
  )
}

const MergedSurveyNotificationsCard: React.FC<MergedSurveyNotificationsCardProps> = ({
  fields,
  control,
  isReadOnly = false,
  isSlotPublishedOrLater = false,
  showDevTools,
  slotId
}) => {
  return (
    <Stack spacing={4}>
      <Box>
        <Typography variant="p5" sx={{ fontWeight: 'bold' }}>Survey Notifications</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Opening, closing, and reminder notifications are combined into one email
        </Typography>
      </Box>

      <Stack spacing={4}>
        {fields.map(({ field, index }) => (
          <SettingRow
            key={field.id}
            field={field}
            index={index}
            control={control}
            isReadOnly={isReadOnly}
            isSlotPublishedOrLater={isSlotPublishedOrLater}
            showDevTools={showDevTools}
            devTaskName={EMAIL_TYPE_TO_TASK[field.emailType]}
            slotId={slotId}
          />
        ))}
      </Stack>

        {/* firstIndex !== undefined && (
          <Stack spacing={1}>
            <Box sx={{ fontSize: '0.9rem' }}>Custom Message</Box>
            <Controller
              name={`settings.${firstIndex}.customMessage`}
              control={control}
              render={({ field }) => (
                <ReactQuill
                  theme="snow"
                  style={{ borderRadius: '4px !important' }}
                  value={field.value}
                  readOnly={isReadOnly}
                  onChange={(value) => {
                    if (isReadOnly) return
                    field.onChange(value)
                    fields.forEach(({ index }) => {
                      if (index !== firstIndex) {
                        setValue(`settings.${index}.customMessage`, value, { shouldDirty: true })
                      }
                    })
                  }}
                />
              )}
            />
            <Box sx={{ fontSize: '0.9rem', color: grey[600] }}>
              (Optional) Custom message you can add to your survey notifications. Leave blank if no custom message
            </Box>
          </Stack>
        ) */}
    </Stack>
  )
}

export default MergedSurveyNotificationsCard
